import { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';

/**
 * Snapshot of one tooth EXACTLY as the interproximal wall fill sees it: the
 * half-edge mesh before any wall face exists, plus the arcs the fill is about
 * to close. Captured in the app (Segmentation dev panel → "Dump Wall
 * Fixtures"), replayed headlessly by `wallFillFixtures.spec.ts`, so real
 * problem teeth become regression tests instead of screenshots.
 */
export interface WallFillFixture {
  jaw: string;
  toothIndex: number;
  /** Flat xyz triples, indexed by the arc entries below. */
  positions: number[];
  /** Triangle vertex indices into `positions`. */
  indices: number[];
  arcs: Array<{
    side: string;
    /** Ordered boundary vertices of the opening, endpoints included. */
    vertices: number[];
  }>;
}

/**
 * Serialises `mesh` plus `arcs` into a replayable fixture. Vertex identity is
 * the mesh's own vertex order, so the arcs stay valid after a round-trip.
 */
export function captureWallFillFixture(
  mesh: HalfEdgeMesh,
  jaw: string,
  toothIndex: number,
  arcs: Array<{ side: string; arc: Vertex[] }>,
): WallFillFixture {
  const indexOf = new Map<Vertex, number>();
  const positions: number[] = [];
  for (const vertex of mesh.halfEdges.vertices) {
    indexOf.set(vertex, indexOf.size);
    positions.push(vertex.position.x, vertex.position.y, vertex.position.z);
  }

  const indices: number[] = [];
  for (const face of mesh.halfEdges.faces) {
    const tri: number[] = [];
    for (const he of face.halfedge.nextLoop()) {
      const index = indexOf.get(he.vertex);
      if (index === undefined) break;
      tri.push(index);
      if (tri.length > 3) break;
    }
    if (tri.length === 3) indices.push(tri[0], tri[1], tri[2]);
  }

  return {
    jaw,
    toothIndex,
    positions,
    indices,
    arcs: arcs.map(({ side, arc }) => ({
      side,
      vertices: arc.map((v) => indexOf.get(v) ?? -1),
    })),
  };
}

/** Triggers a browser download of the collected fixtures as one JSON file. */
export function downloadWallFillFixtures(fixtures: WallFillFixture[], filename: string): void {
  if (fixtures.length === 0 || typeof document === 'undefined') return;
  const blob = new Blob([JSON.stringify({ fixtures })], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
